import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Public slide-width tokens. Not exported as `as const` array literals so the
 * authoring check reads each CVA map on its own (breakpoint maps omit the preset).
 */
const carouselItemBasisTokenList = [
  'full',
  'half',
  'third',
  'quarter',
  'fifth',
  // Phone peek: 88% width so ~6% of each neighbor shows when centered.
  'peek',
  'card-sm',
  'card-md',
  'responsive',
] as const;

const carouselItemBasisBreakpointTokenList = [
  'full',
  'half',
  'third',
  'quarter',
  'fifth',
  'peek',
  'card-sm',
  'card-md',
] as const;

export const CAROUSEL_ITEM_BASIS_TOKENS: readonly (typeof carouselItemBasisTokenList)[number][] =
  carouselItemBasisTokenList;

export const CAROUSEL_ITEM_BASIS_BREAKPOINT_TOKENS: readonly (typeof carouselItemBasisBreakpointTokenList)[number][] =
  carouselItemBasisBreakpointTokenList;

export type CarouselItemBasisToken =
  (typeof carouselItemBasisTokenList)[number];

export type CarouselItemBasisBreakpointToken =
  (typeof carouselItemBasisBreakpointTokenList)[number];

const carouselItemBasisClasses: Record<CarouselItemBasisToken, string> = {
  full: 'basis-full',
  // One full slide below md, two slides from md up.
  half: 'basis-full md:basis-1/2',
  third: 'basis-1/3',
  quarter: 'basis-1/4',
  fifth: 'basis-1/5',
  peek: 'basis-[88%]',
  'card-sm': 'basis-auto w-carousel-slot-sm',
  'card-md': 'basis-auto w-carousel-slot-md',
  responsive: 'basis-full sm:basis-1/2 lg:basis-1/4',
};

const carouselItemBasisMdClasses: Record<
  CarouselItemBasisBreakpointToken,
  string
> = {
  full: 'md:basis-full md:w-auto',
  half: 'md:basis-1/2 md:w-auto',
  third: 'md:basis-1/3 md:w-auto',
  quarter: 'md:basis-1/4 md:w-auto',
  fifth: 'md:basis-1/5 md:w-auto',
  peek: 'md:basis-[88%] md:w-auto',
  'card-sm': 'md:basis-auto md:w-carousel-slot-sm',
  'card-md': 'md:basis-auto md:w-carousel-slot-md',
};

const carouselItemBasisLgClasses: Record<
  CarouselItemBasisBreakpointToken,
  string
> = {
  full: 'lg:basis-full lg:w-auto',
  half: 'lg:basis-1/2 lg:w-auto',
  third: 'lg:basis-1/3 lg:w-auto',
  quarter: 'lg:basis-1/4 lg:w-auto',
  fifth: 'lg:basis-1/5 lg:w-auto',
  peek: 'lg:basis-[88%] lg:w-auto',
  'card-sm': 'lg:basis-auto lg:w-carousel-slot-sm',
  'card-md': 'lg:basis-auto lg:w-carousel-slot-md',
};

/**
 * HEN Carousel item variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const carouselItemVariants = cva(['min-w-0 shrink-0 grow-0'], {
  variants: {
    basis: carouselItemBasisClasses,
    'basis-md': carouselItemBasisMdClasses,
    'basis-lg': carouselItemBasisLgClasses,
  },
  defaultVariants: {
    basis: 'full',
  },
});

export type CarouselItemVariantProps = VariantProps<
  typeof carouselItemVariants
>;

/** When mounted arrows are visible. Ignored when `showControls` is false. */
export const CAROUSEL_ARROW_REVEAL = ['always', 'hover'] as const;

export type CarouselArrowReveal = (typeof CAROUSEL_ARROW_REVEAL)[number];

/**
 * What to do with an arrow that cannot move.
 * `dim` keeps it on the page. `hide` removes it.
 */
export const CAROUSEL_UNAVAILABLE_ARROW = ['dim', 'hide'] as const;

export type CarouselUnavailableArrow =
  (typeof CAROUSEL_UNAVAILABLE_ARROW)[number];

/**
 * Where mounted arrows sit. When `showControls` is false, only `header` has an
 * effect: it keeps the Carousel `header` heading above the slides.
 */
export const CAROUSEL_ARROW_POSITION = [
  'bottom',
  'inside',
  'beside',
  'header',
] as const;

export type CarouselArrowPosition = (typeof CAROUSEL_ARROW_POSITION)[number];

/** Public arrow designs authors can select. */
export const CAROUSEL_ARROW_DESIGNS = [
  'default',
  'outline',
  'onDark',
  'solid',
] as const;

export type CarouselArrowDesign = (typeof CAROUSEL_ARROW_DESIGNS)[number];

/** Public arrow sizes authors can select: 24px, 36px, 44px, and 48px circles. */
export const CAROUSEL_ARROW_SIZES = ['sm', 'md', 'lg', 'xl'] as const;

export type CarouselArrowSize = (typeof CAROUSEL_ARROW_SIZES)[number];

/** Whether each slide keeps its own height or matches the tallest slide. */
export const CAROUSEL_SLIDE_HEIGHT = ['auto', 'equal'] as const;

export type CarouselSlideHeight = (typeof CAROUSEL_SLIDE_HEIGHT)[number];

/**
 * Carousel navigation button (previous/next) variants.
 * - `default`: black outline that fills black with white text on hover
 * - `outline`: transparent background, border only; no fill on hover
 * - `onDark`: white fill with a dark icon, for dark surfaces that set white text.
 *   Hover darkens the circle and keeps the icon dark. `hover:!bg-*` beats the
 *   shadcn outline button's `hover:bg-neutral-100` (CVA does not twMerge).
 *   Press uses the same zoom-out as MediaGallery tiles. Motion, cursor, and
 *   disabled-hover locks stay on this design only so other arrow designs keep
 *   shadcn's default transition and hover.
 *   Disabled arrows dim to a dark fill.
 * - `solid`: black fill with a white icon
 */
export const carouselNavButtonVariants = cva('static rounded-full border', {
  variants: {
    design: {
      default:
        'border-black hover:bg-black hover:text-white hover:border-white',
      outline:
        'bg-transparent border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:border-neutral-900 hover:text-neutral-200',
      onDark: [
        'bg-white border-white text-neutral-900 cursor-pointer',
        // Same press zoom + ease as MediaGallery image tiles (onDark only).
        'transition-[color,background-color,border-color,transform] duration-slower ease-emphasized',
        'motion-reduce:transition-none',
        'active:scale-press-zoom-out motion-reduce:active:scale-100',
        // Important: win over shadcn outline `hover:bg-neutral-100`.
        // Use neutral-200 — the theme has no neutral-300, so that utility
        // never emits and Back/arrows stay white on hover in Storybook.
        'hover:!bg-neutral-200 hover:!border-neutral-200 hover:text-neutral-900',
        'dark:bg-white dark:border-white dark:text-neutral-900',
        'dark:hover:!bg-neutral-200 dark:hover:!border-neutral-200 dark:hover:text-neutral-900',
        'ring-offset-neutral-900 focus-visible:ring-white',
        'aria-disabled:bg-neutral-700 aria-disabled:border-neutral-700 aria-disabled:opacity-40',
        'dark:aria-disabled:bg-neutral-700 dark:aria-disabled:border-neutral-700',
        // Hover uses !important; keep the dim fill on unavailable arrows.
        'aria-disabled:hover:!bg-neutral-700 aria-disabled:hover:!border-neutral-700',
        'dark:aria-disabled:hover:!bg-neutral-700 dark:aria-disabled:hover:!border-neutral-700',
        // Unavailable beside arrows stay pointer-reachable over a dismissible
        // field; kill press zoom and the pointer cursor so they read as inert.
        'aria-disabled:active:scale-100 aria-disabled:cursor-default',
      ].join(' '),
      solid:
        'bg-black border-black text-white hover:bg-neutral-700 hover:border-neutral-700 hover:text-white',
    } satisfies Record<CarouselArrowDesign, string>,
    size: {
      sm: 'h-6 w-6',
      md: 'h-9 w-9 [&_svg]:size-5',
      lg: 'h-11 w-11 [&_svg]:size-5',
      xl: 'h-12 w-12 [&_svg]:size-6',
    } satisfies Record<CarouselArrowSize, string>,
  },
  defaultVariants: {
    design: 'default',
    size: 'sm',
  },
});

/**
 * Invisible 44px hit area centered on an arrow, so the smaller circles still
 * meet the minimum touch target. The arrow must be positioned (`relative` or
 * `absolute`) for the pseudo-element to anchor to it.
 */
export const carouselArrowHitAreaClassName =
  'after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full';

export type CarouselNavButtonVariantProps = VariantProps<
  typeof carouselNavButtonVariants
>;

/** Public pagination-dot designs authors can select. */
export const CAROUSEL_DOT_DESIGNS = ['default', 'outline', 'pill'] as const;

export type CarouselDotDesign = (typeof CAROUSEL_DOT_DESIGNS)[number];

/**
 * Dot color for the `pill` design.
 * - `light`: white / light gray glyphs (for dark photos or a dark track)
 * - `dark`: charcoal glyphs (for light surfaces)
 */
export const CAROUSEL_DOT_TONES = ['light', 'dark'] as const;

export type CarouselDotTone = (typeof CAROUSEL_DOT_TONES)[number];

/**
 * Pagination-dot size. `default` keeps today's geometry. `compact` uses a
 * shorter active pill (~12×6) and smaller inactive dots (~6px), with a
 * ~14px pill track.
 */
export const CAROUSEL_DOT_SIZES = ['default', 'compact'] as const;

export type CarouselDotSize = (typeof CAROUSEL_DOT_SIZES)[number];

/**
 * Horizontal inset for Previous/Next when `arrowPosition` is `inside`.
 * `none` keeps today's flush `left-0` / `right-0` edges.
 */
export const CAROUSEL_ARROW_INSET = ['none', 'sm', 'md', 'lg'] as const;

export type CarouselArrowInset = (typeof CAROUSEL_ARROW_INSET)[number];

/** Tailwind classes for inside-arrow left/right inset tokens. */
export const carouselArrowInsetClasses: Record<
  CarouselArrowInset,
  { previous: string; next: string }
> = {
  none: { previous: 'left-0', next: 'right-0' },
  sm: { previous: 'left-sm', next: 'right-sm' },
  md: { previous: 'left-md', next: 'right-md' },
  lg: { previous: 'left-lg', next: 'right-lg' },
};

/** Room around the slides so card shadows are not clipped by the viewport; the
 * neighbouring slide also shows that far past the carousel edge. */
export const CAROUSEL_SHADOW_SPACE = ['none', 'sm', 'md'] as const;

export type CarouselShadowSpace = (typeof CAROUSEL_SHADOW_SPACE)[number];

/** Padding + negative-margin pairs grow the clip box without moving the layout. Fixed
 * px, not spacing tokens (those shrink on small screens); needs that much page gutter. */
export const carouselShadowSpaceClasses: Record<CarouselShadowSpace, string> = {
  none: '',
  sm: '-mx-2 px-2 -my-2 py-2',
  md: '-mx-4 px-4 -my-4 py-4',
};

/** Above and below only, for beside arrows: side room would slide cards under them. */
export const carouselShadowSpaceVerticalClasses: Record<
  CarouselShadowSpace,
  string
> = {
  none: '',
  sm: '-my-2 py-2',
  md: '-my-4 py-4',
};

/**
 * Wrapper around the pagination dots.
 * - `default` / `outline`: open row under the slides (unchanged)
 * - `pill`: no gap or side padding, because each dot's hit cell carries its
 *   own spacing. Vertical padding is always on so the capsule clears the
 *   glyphs (standard active glyph is 8px; `py-xxs` is 6px on mobile and
 *   8px from the larger breakpoint up). Compact shortens the track but
 *   keeps a 4px pad.
 */
export const carouselDotsTrackVariants = cva(
  'flex items-center justify-center',
  {
    variants: {
      design: {
        default: 'gap-5',
        outline: 'gap-5',
        // py-xxs: 6px mobile / 8px larger — clears the 8px active glyph on md+.
        pill: 'relative py-xxs',
      } satisfies Record<CarouselDotDesign, string>,
      size: {
        default: '',
        compact: '',
      } satisfies Record<CarouselDotSize, string>,
    },
    compoundVariants: [
      {
        design: 'pill',
        size: 'compact',
        // 4px pad + up to 6px glyph ≈ 14px bar; cn() drops conflicting py-xxs.
        class: 'min-h-[14px] py-min',
      },
    ],
    defaultVariants: {
      design: 'default',
      size: 'default',
    },
  },
);

/**
 * Absolute capsule behind pill dots when `showDotBackground` is on. It
 * contrasts with the dot tone: dark behind light dots, light behind dark dots.
 * Opacity sits on this layer, not a `/80` color modifier: HEN colors are CSS
 * variables, and Tailwind emits no rule for opacity modifiers on them.
 * Colors read `--cds-carousel-pill-dots-bg` so tenants can recolor pill
 * pagination without changing solid dots or global neutrals; unset keeps
 * today's tone-specific fills.
 */
export const carouselDotsBackgroundVariants = cva(
  'pointer-events-none absolute inset-0 rounded-full opacity-80',
  {
    variants: {
      tone: {
        light:
          'bg-[var(--cds-carousel-pill-dots-bg,var(--neutral-900,#1C1E21))]',
        dark: 'bg-[var(--cds-carousel-pill-dots-bg,var(--neutral-white,#FFFFFF))]',
      } satisfies Record<CarouselDotTone, string>,
    },
    defaultVariants: {
      tone: 'light',
    },
  },
);

/**
 * Button around each pill glyph. Adjacent cells touch but never overlap, so a
 * click always selects the dot under the pointer. The 8px side padding makes
 * every cell at least 24px wide (WCAG 2.5.8 target size), and the 24px-tall
 * `after` layer gives the same height without reaching a neighbor. Compact
 * keeps the 24px hit cell via `min-w-6` while the track shrinks to ~14px.
 */
export const carouselPillDotCellVariants = cva(
  [
    'relative z-[1] flex h-2 cursor-default items-center justify-center rounded-full px-xxs',
    'after:absolute after:inset-x-0 after:top-1/2 after:h-6 after:-translate-y-1/2',
    'focus-visible:outline-none focus-visible:ring-2',
  ],
  {
    variants: {
      tone: {
        light: 'focus-visible:ring-white',
        dark: 'focus-visible:ring-neutral-900',
      } satisfies Record<CarouselDotTone, string>,
      size: {
        default: '',
        compact: 'min-w-6 px-0',
      } satisfies Record<CarouselDotSize, string>,
    },
    defaultVariants: {
      tone: 'light',
      size: 'default',
    },
  },
);

/**
 * 24px hit cell for compact default / outline dots (glyph sits inside).
 * Pill uses `carouselPillDotCellVariants` instead.
 */
export const carouselCompactDotCellClassName = [
  'group relative flex h-6 w-6 cursor-default items-center justify-center rounded-full',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
].join(' ');

/**
 * Pill does not restyle on hover. Unselected pills use the round default
 * token, with a tone-specific fallback when it is unset.
 */
export const carouselDotVariants = cva(
  [
    'h-2.5 w-2.5 rounded-full transition-all',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
  ],
  {
    variants: {
      design: {
        default: '',
        outline: '',
        pill: 'block h-2 w-2',
      } satisfies Record<CarouselDotDesign, string>,
      tone: {
        light: '',
        dark: '',
      } satisfies Record<CarouselDotTone, string>,
      active: {
        true: '',
        false: '',
      },
      size: {
        default: '',
        compact: '',
      } satisfies Record<CarouselDotSize, string>,
    },
    compoundVariants: [
      { design: 'default', active: true, class: 'bg-carousel-dot-active' },
      {
        design: 'default',
        active: false,
        class: 'bg-carousel-dot-inactive',
      },
      {
        design: 'default',
        size: 'default',
        active: false,
        class: 'hover:bg-carousel-dot-hover',
      },
      {
        design: 'default',
        size: 'compact',
        active: false,
        class: 'group-hover:bg-carousel-dot-hover',
      },
      {
        design: 'outline',
        active: true,
        class: 'bg-carousel-dot-active border border-carousel-dot-active',
      },
      {
        design: 'outline',
        active: false,
        class: 'bg-transparent border border-carousel-dot-active',
      },
      {
        design: 'outline',
        size: 'default',
        active: false,
        class: 'hover:bg-carousel-dot-hover',
      },
      {
        design: 'outline',
        size: 'compact',
        active: false,
        class: 'group-hover:bg-carousel-dot-hover',
      },
      // Size-scoped so compact can replace `w-5` without fighting this compound.
      { design: 'pill', size: 'default', active: true, class: 'w-5' },
      {
        design: 'pill',
        tone: 'light',
        active: true,
        class:
          'bg-[var(--cds-carousel-indicator-pill-active,var(--neutral-white,#FFFFFF))]',
      },
      {
        design: 'pill',
        tone: 'light',
        active: false,
        class:
          'bg-[var(--cds-carousel-indicator-round-default,var(--neutral-200,#B1B8C3))]',
      },
      {
        design: 'pill',
        tone: 'dark',
        active: true,
        class:
          'bg-[var(--cds-carousel-indicator-pill-active,var(--neutral-900,#1C1E21))]',
      },
      {
        design: 'pill',
        tone: 'dark',
        active: false,
        class:
          'bg-[var(--cds-carousel-indicator-round-default,var(--neutral-500,#434953))]',
      },
      {
        design: 'pill',
        size: 'compact',
        active: true,
        class: 'h-1.5 w-3',
      },
      {
        design: 'pill',
        size: 'compact',
        active: false,
        class: 'h-1.5 w-1.5',
      },
      {
        design: 'default',
        size: 'compact',
        active: true,
        class: 'h-1.5 w-3',
      },
      {
        design: 'default',
        size: 'compact',
        active: false,
        class: 'h-1.5 w-1.5',
      },
      {
        design: 'outline',
        size: 'compact',
        active: true,
        class: 'h-1.5 w-3',
      },
      {
        design: 'outline',
        size: 'compact',
        active: false,
        class: 'h-1.5 w-1.5',
      },
    ],
    defaultVariants: {
      design: 'default',
      tone: 'light',
      active: false,
      size: 'default',
    },
  },
);

export type CarouselDotVariantProps = VariantProps<typeof carouselDotVariants>;
